package ai.wagemix.logic.pricing

import ai.wagemix.data.touchesUser
import ai.wagemix.data.WMAuthSyncEvent
import ai.wagemix.data.WMBillingSyncEvent
import ai.wagemix.logic.entitledPlan
import ai.wagemix.logic.lapsedPlan
import ai.wagemix.logic.isGlobal
import ai.wagemix.logic.pricing.WMPricingPresenter.Action
import ai.wagemix.logic.pricing.WMPricingPresenter.Event
import ai.wagemix.logic.pricing.WMPricingPresenter.Prompt
import ai.wagemix.logic.session
import ai.wagemix.logic.WMNavigationRequest
import ai.wagemix.logic.WMRoutes
import ai.wagemix.net.models.firebase.WMSubscriptionType
import ai.wagemix.net.models.WMPlanPrice
import com.fsryan.foundation.logic.FSPresenter
import com.fsryan.foundation.logic.FSPrompt
import com.fsryan.foundation.logic.presenterInstanceId
import com.fsryan.foundation.logic.prompting.defaultHandlePromptResponse
import com.fsryan.foundation.logic.prompting.FSBasePromptingPresenter
import com.fsryan.foundation.logic.prompting.FSPromptingPresenter
import com.fsryan.foundation.models.FSPropertyContainer
import com.fsryan.foundation.util.log.FSLog
import kotlin.jvm.JvmInline
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.onEach

/** Whether checkout may start for this visitor. */
enum class WMPricingAccess { LoggedOut, EmailUnverified, Ready }

data class WMPricingPresentation(
    val prices: List<WMPlanPrice>,
    val currentPlan: WMSubscriptionType,
    val access: WMPricingAccess,
    /** The paid plan a lapsed account had, which the page offers to renew; `null` for every other visitor. */
    val renewPlan: WMSubscriptionType? = null,
) {
    /** The free plan is the visitor's own: signed in without a paid plan. */
    val freeIsCurrent: Boolean get() = access != WMPricingAccess.LoggedOut && currentPlan == WMSubscriptionType.None

    /**
     * Only a logged-out visitor can start on the free plan, by creating an account; a signed-in user is on it already
     * or pays for more (a paid plan ends through the billing portal, not here).
     */
    val canStartFree: Boolean get() = access == WMPricingAccess.LoggedOut
}

/** Pricing: live prices, the current plan, verified-email gating, checkout. */
interface WMPricingPresenter<P: Prompt, out E: Event<P>, in A: Action>: FSPromptingPresenter<P, E, A> {

    interface Action: FSPromptingPresenter.Action {
        sealed interface Base: Action

        @JvmInline
        value class ComposedSuperAction internal constructor(val wrapped: FSPromptingPresenter.Action.Base): Base

        @ConsistentCopyVisibility
        data class StartCheckout internal constructor(val lookupKey: String, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<StartCheckout> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        @ConsistentCopyVisibility
        data class ResendVerification internal constructor(override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<ResendVerification> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        interface Factory<P: Prompt, out A: Action>: FSPromptingPresenter.Action.Factory<P, A> {
            fun createStartCheckout(lookupKey: String, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createResendVerification(extendedProperties: Map<String, Any> = emptyMap()): A
        }

        companion object {
            fun <P: Prompt> baseFactory(): Factory<P, Base> = object: Factory<P, Base> {
                private val base = FSPromptingPresenter.Action.baseFactory<P>()
                override fun createUserRespondedToPrompt(response: FSPrompt.Response<P>, extendedProperties: Map<String, Any>): Base = ComposedSuperAction(base.createUserRespondedToPrompt(response, extendedProperties))
                override fun createStartCheckout(lookupKey: String, extendedProperties: Map<String, Any>): Base = StartCheckout(lookupKey, extendedProperties)
                override fun createResendVerification(extendedProperties: Map<String, Any>): Base = ResendVerification(extendedProperties)
            }
        }
    }

    interface Event<out P: Prompt>: FSPromptingPresenter.Event<P> {
        val pricingUpdate: WMPricingPresentation?
        val navigationUpdate: WMNavigationRequest?

        @ConsistentCopyVisibility
        data class Base<out P: Prompt> internal constructor(
            private val base: FSPromptingPresenter.Event.Base<P>,
            override val pricingUpdate: WMPricingPresentation? = null,
            override val navigationUpdate: WMNavigationRequest? = null
        ): Event<P>, FSPropertyContainer<Base<P>>, FSPromptingPresenter.Event<P> by base {
            override val extendedProperties: Map<String, Any> get() = base.extendedProperties
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(base = base.replaceExtendedProperties(properties))
        }

        interface Factory<P: Prompt, out E: Event<P>>: FSPromptingPresenter.Event.Factory<P, E> {
            fun createPricingUpdate(pricing: WMPricingPresentation): E
            fun createNavigation(request: WMNavigationRequest): E
        }

        companion object {
            fun <P: Prompt> baseFactory(): Factory<P, Base<P>> = object: Factory<P, Base<P>> {
                private val base = FSPromptingPresenter.Event.baseFactory<P>()
                override fun createPromptChange(prompt: P, loading: Boolean?, extendedProperties: Map<String, Any>) = Base(base.createPromptChange(prompt, loading, extendedProperties))
                override fun createLoadingChange(loading: Boolean, extendedProperties: Map<String, Any>) = Base(base.createLoadingChange(loading, extendedProperties))
                override fun none() = Base(base.none())
                override fun createPricingUpdate(pricing: WMPricingPresentation) = Base(base.none(), pricingUpdate = pricing)
                override fun createNavigation(request: WMNavigationRequest) = Base(base.none(), navigationUpdate = request)
            }
        }
    }

    interface Prompt: FSPromptingPresenter.Prompt {
        sealed interface Base: Prompt

        @JvmInline
        value class ComposedSuperPrompt internal constructor(val wrapped: FSPromptingPresenter.Prompt.Base): Base

        data object SignInToSubscribe: Base, FSPrompt.Displayed {
            override val forceInteraction = false
            override val messageOverride: String? = null
            override val titleOverride: String? = null
            enum class Selection { Dismiss, SignIn, Register }
            fun selectionOf(selected: Int): Selection? = Selection.entries.getOrNull(selected)
        }

        @ConsistentCopyVisibility
        data class VerifyEmailFirst internal constructor(val email: String): Base, FSPrompt.Displayed {
            override val forceInteraction = false
            override val messageOverride: String? = null
            override val titleOverride: String? = null
            enum class Selection { Dismiss, Resend }
            fun selectionOf(selected: Int): Selection? = Selection.entries.getOrNull(selected)
        }

        @ConsistentCopyVisibility
        data class VerificationSent internal constructor(val email: String): Base, FSPrompt.Displayed {
            override val forceInteraction = false
            override val messageOverride: String? = null
            override val titleOverride: String? = null
        }

        interface Factory<out P: Prompt>: FSPromptingPresenter.Prompt.Factory<P> {
            fun signInToSubscribe(): P
            fun verifyEmailFirst(email: String): P
            fun verificationSent(email: String): P
        }

        companion object {
            fun baseFactory(): Factory<Base> = object: Factory<Base> {
                override fun createBase(wrapped: FSPrompt.Base): Base = ComposedSuperPrompt(FSPromptingPresenter.Prompt.baseFactory().createBase(wrapped))
                override fun signInToSubscribe(): Base = SignInToSubscribe
                override fun verifyEmailFirst(email: String): Base = VerifyEmailFirst(email)
                override fun verificationSent(email: String): Base = VerificationSent(email)
            }
        }
    }

    fun userRequestedCheckout(lookupKey: String, extendedProperties: Map<String, Any> = emptyMap())
    fun userRequestedResendVerification(extendedProperties: Map<String, Any> = emptyMap())

    companion object
}

abstract class WMBasePricingPresenter<
    P: Prompt,
    E: Event<P>,
    A: Action,
    EF: Event.Factory<P, E>,
    AF: Action.Factory<P, A>,
    PF: Prompt.Factory<P>,
    G: WMPricingDataGateway
>(
    protected val dataGateway: G,
    eventFactory: EF,
    actionFactory: AF,
    promptFactory: PF,
    cancelViewScopeOnUnready: Boolean,
    instanceId: String,
    handlePromptResponse: suspend (FSPrompt.Response<P>, suspend E.() -> Unit) -> Unit = { response, emit ->
        context(eventFactory, promptFactory, dataGateway) { defaultPricingHandlePromptResponse(response, emit) }
    }
): FSBasePromptingPresenter<P, E, A, EF, AF, PF>(cancelViewScopeOnUnready, instanceId, eventFactory, actionFactory, promptFactory, handlePromptResponse),
    WMPricingPresenter<P, E, A> {

    override fun userRequestedCheckout(lookupKey: String, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createStartCheckout(lookupKey, extendedProperties))
    override fun userRequestedResendVerification(extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createResendVerification(extendedProperties))

    override fun registerSubscriptions() {
        dataGateway.cacheUpdateFlow
            .filter { it.touchesUser() }
            .onEach { emitPricing() }
            .launchInViewScope()

        dataGateway.contextUpdateFlow
            .filter { it.isGlobal && it.planPricesChanged() }
            .onEach { emitPricing() }
            .launchInViewScope()

        dataGateway.billingSyncEventFlow
            .filter { it.presenterInstanceId() == instanceId }
            .onEach { event ->
                when (event) {
                    is WMBillingSyncEvent.StartCheckout.Initial -> eventFactory.createLoadingChange(true).emit()
                    is WMBillingSyncEvent.FetchPrices.Initial -> eventFactory.createLoadingChange(true).emit()
                    is WMBillingSyncEvent.StartCheckout.Success -> {
                        eventFactory.createLoadingChange(false).emit()
                        // The sync put the Checkout URL into the global context; follow it once and forget it.
                        // `checkout_started` is the API's, which made the session (analytics plan §5a).
                        dataGateway.retrieveGlobalContext().checkoutUrl(event.lookupKey)?.let { url ->
                            dataGateway.deleteCheckoutUrl(event.lookupKey)
                            eventFactory.createNavigation(WMNavigationRequest.External(url)).emit()
                        }
                    }
                    is WMBillingSyncEvent.FetchPrices.Success -> eventFactory.createLoadingChange(false).emit()
                    is WMBillingSyncEvent.StartCheckout.Failure, is WMBillingSyncEvent.FetchPrices.Failure -> {
                        eventFactory.createPromptChange(promptFactory.networkError(), loading = false).emit()
                    }
                    else -> Unit
                }
            }
            .launchInViewScope()

        dataGateway.authSyncEventFlow
            .filter { it.presenterInstanceId() == instanceId }
            .onEach { event ->
                when (event) {
                    is WMAuthSyncEvent.SendEmailVerification.Initial -> eventFactory.createLoadingChange(true).emit()
                    is WMAuthSyncEvent.SendEmailVerification.Success -> {
                        val email = dataGateway.session()?.email ?: ""
                        eventFactory.createPromptChange(promptFactory.verificationSent(email), loading = false).emit()
                    }
                    is WMAuthSyncEvent.SendEmailVerification.Failure -> {
                        eventFactory.createPromptChange(promptFactory.networkError(), loading = false).emit()
                    }
                    else -> Unit
                }
            }
            .launchInViewScope()
    }

    override suspend fun sendInitialEvents(isFirstOnReadyCall: Boolean) {
        emitPricing()
        if (dataGateway.retrieveGlobalContext().planPrices() == null) {
            dataGateway.fetchPrices(createPresenterContext())
        }
    }

    /** The plan the price with [lookupKey] buys, from the price list the page shows. */
    private suspend fun planOf(lookupKey: String): WMSubscriptionType? {
        return dataGateway.retrieveGlobalContext().planPrices()?.firstOrNull { it.lookupKey == lookupKey }?.plan
    }

    private suspend fun emitPricing() {
        val session = dataGateway.session()
        // A lapsed account has no current plan here, so it can buy back the one it lapsed from.
        val plan = dataGateway.entitledPlan()
        val access = when {
            session == null -> WMPricingAccess.LoggedOut
            !session.emailVerified -> WMPricingAccess.EmailUnverified
            else -> WMPricingAccess.Ready
        }
        val prices = dataGateway.retrieveGlobalContext().planPrices() ?: emptyList()
        val presentation = WMPricingPresentation(prices, plan, access, renewPlan = dataGateway.lapsedPlan())
        eventFactory.createPricingUpdate(presentation).emit()
    }

    override fun extractBasePromptingPresenterAction(action: A): FSPromptingPresenter.Action.Base? = (action as? Action.ComposedSuperAction)?.wrapped

    @Suppress("UNCHECKED_CAST")
    override fun FSPrompt.Response<*>.extract(): FSPrompt.Response<P> = this as FSPrompt.Response<P>

    override fun handleNonBasePromptingPresenterAction(action: A) {
        when (action) {
            is Action.StartCheckout -> launch {
                val session = dataGateway.session()
                val currentPlan = dataGateway.entitledPlan()
                FSLog.sendWMPlanSelected(plan = planOf(action.lookupKey), currentPlan = currentPlan)
                when {
                    session == null -> eventFactory.createPromptChange(promptFactory.signInToSubscribe()).emit()
                    !session.emailVerified -> eventFactory.createPromptChange(promptFactory.verifyEmailFirst(session.email)).emit()
                    else -> dataGateway.startCheckout(action.lookupKey, createPresenterContext(action))
                }
            }
            is Action.ResendVerification -> dataGateway.sendEmailVerification(createPresenterContext(action))
            else -> logUnknownActionType(action)
        }
    }
}

context(eventFactory: EF, promptFactory: PF, dataGateway: G)
suspend fun <
    P: Prompt,
    E: Event<P>,
    EF: Event.Factory<P, E>,
    PF: Prompt.Factory<P>,
    G: WMPricingDataGateway
> defaultPricingHandlePromptResponse(response: FSPrompt.Response<P>, emit: suspend E.() -> Unit) {
    when (val prompt = response.prompt) {
        is Prompt.SignInToSubscribe -> {
            eventFactory.createPromptChange(promptFactory.none()).emit()
            when (prompt.selectionOf(response.selected)) {
                Prompt.SignInToSubscribe.Selection.SignIn -> eventFactory.createNavigation(WMNavigationRequest.Route("${WMRoutes.LOGIN}?next=${WMRoutes.PRICING}")).emit()
                Prompt.SignInToSubscribe.Selection.Register -> eventFactory.createNavigation(WMNavigationRequest.Route(WMRoutes.REGISTER)).emit()
                else -> Unit
            }
        }
        is Prompt.VerifyEmailFirst -> {
            if (prompt.selectionOf(response.selected) == Prompt.VerifyEmailFirst.Selection.Resend) {
                eventFactory.createPromptChange(promptFactory.none(), loading = true).emit()
                dataGateway.sendEmailVerification()
            } else {
                eventFactory.createPromptChange(promptFactory.none()).emit()
            }
        }
        is Prompt.VerificationSent -> eventFactory.createPromptChange(promptFactory.none()).emit()
        else -> defaultHandlePromptResponse(response, emit)
    }
}

typealias WMDefaultPricingPresenter = WMPricingPresenter<Prompt.Base, Event.Base<Prompt.Base>, Action.Base>

fun WMPricingPresenter.Companion.createDefault(
    dataGateway: WMPricingDataGateway,
    instanceId: String = FSPresenter.createInstanceId("WMPricingPresenter")
): WMDefaultPricingPresenter = object: WMBasePricingPresenter<
    Prompt.Base,
    Event.Base<Prompt.Base>,
    Action.Base,
    Event.Factory<Prompt.Base, Event.Base<Prompt.Base>>,
    Action.Factory<Prompt.Base, Action.Base>,
    Prompt.Factory<Prompt.Base>,
    WMPricingDataGateway
>(
    dataGateway = dataGateway,
    eventFactory = Event.baseFactory(),
    actionFactory = Action.baseFactory(),
    promptFactory = Prompt.baseFactory(),
    cancelViewScopeOnUnready = true,
    instanceId = instanceId
) {}
